import { useState, useEffect, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useProcedureCodeByCode } from '@/hooks/useProcedureCodes';
import type { DentalShortcutRow } from '@/hooks/useDentalShortcuts';

// ── Props ─────────────────────────────────────────────────────────────────────

interface QuickRegistrationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  clinicId?: string;
  shortcut: DentalShortcutRow | null;
  toothFdis: string[];
}

const SURFACES = ['B', 'L', 'M', 'D', 'O'] as const;
type Surface = (typeof SURFACES)[number];

// ── Component ─────────────────────────────────────────────────────────────────

export function QuickRegistrationDrawer({
  isOpen,
  onClose,
  patientId,
  shortcut,
  toothFdis,
}: QuickRegistrationDrawerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: procedureCode } = useProcedureCodeByCode(shortcut?.procedure_code ?? null);

  const [selectedSurfaces, setSelectedSurfaces] = useState<Surface[]>([]);
  const [diagnosis, setDiagnosis] = useState('');
  const [price, setPrice] = useState<string>('');
  const [visitNumber, setVisitNumber] = useState(1);
  const [keepOpen, setKeepOpen] = useState(false);
  const [teeth, setTeeth] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Sync teeth from props
  useEffect(() => {
    setTeeth(toothFdis);
  }, [toothFdis]);

  // Pre-fill price when procedure code or shortcut changes
  useEffect(() => {
    if (procedureCode?.default_price_dkk != null) {
      setPrice(String(procedureCode.default_price_dkk));
    } else {
      setPrice('');
    }
  }, [procedureCode?.default_price_dkk]);

  // ESC closes
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const toggleSurface = (s: Surface) => {
    setSelectedSurfaces((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  const removeTooth = (fdi: string) => {
    setTeeth((prev) => prev.filter((t) => t !== fdi));
  };

  const handleSave = async () => {
    if (!shortcut || !procedureCode || teeth.length === 0) return;

    const requiresSurfaces = procedureCode.requires_surfaces;
    if (requiresSurfaces && selectedSurfaces.length === 0) {
      toast.error(t('dental.shortcuts.quickRegistration.surfacesRequired'));
      return;
    }

    setIsSaving(true);
    try {
      for (const fdi of teeth) {
        const { error } = await supabase.rpc('upsert_treatment_plan', {
          p_patient_id: patientId,
          p_tooth_fdi: fdi,
          p_treatment_type: procedureCode.code,
          p_treatment_description: procedureCode.name_da,
          p_surfaces_treated: selectedSurfaces.length > 0 ? selectedSurfaces : undefined,
          p_diagnosis: diagnosis || undefined,
          p_estimated_cost: price ? Number(price) : undefined,
          p_visit_number: visitNumber,
        });
        if (error) throw error;
      }

      if (teeth.length === 1) {
        toast.success(
          t('dental.shortcuts.quickRegistration.savedSingle', { fdi: teeth[0] }),
        );
      } else {
        toast.success(
          t('dental.shortcuts.quickRegistration.savedMulti', { count: teeth.length }),
        );
      }

      // Reset form fields
      setSelectedSurfaces([]);
      setDiagnosis('');
      setVisitNumber(1);

      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Ukendt fejl';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const requiresSurfaces = procedureCode?.requires_surfaces ?? false;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-black/30 transition-opacity duration-200',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('dental.shortcuts.quickRegistration.title')}
        className={cn(
          'fixed right-0 top-0 z-40 h-full w-full max-w-[420px]',
          'bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700',
          'flex flex-col overflow-hidden',
          'transform transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 flex-shrink-0">
          <div className="flex items-center gap-2">
            {shortcut && (
              <span
                className="h-3 w-3 rounded-full inline-block"
                style={{ backgroundColor: shortcut.color }}
              />
            )}
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('dental.shortcuts.quickRegistration.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Procedure code badge */}
          {procedureCode && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2.5">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">
                Ydelseskode
              </p>
              <div className="flex items-baseline gap-2">
                <span
                  className="rounded px-1.5 py-0.5 text-xs font-mono font-semibold text-white"
                  style={{ backgroundColor: shortcut?.color ?? '#6366f1' }}
                >
                  {procedureCode.code}
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {procedureCode.name_da}
                </span>
              </div>
            </div>
          )}

          {/* Selected teeth */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {t('dental.shortcuts.quickRegistration.selectedTeeth')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {teeth.map((fdi) => (
                <span
                  key={fdi}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200"
                >
                  {fdi}
                  {teeth.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTooth(fdi)}
                      className="ml-0.5 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Surfaces */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {t('dental.shortcuts.quickRegistration.surfaces')}
              {requiresSurfaces && (
                <span className="ml-1 text-red-500">*</span>
              )}
            </label>
            <div className="flex gap-1.5">
              {SURFACES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSurface(s)}
                  className={cn(
                    'h-9 w-9 rounded-lg border text-sm font-semibold transition-colors',
                    selectedSurfaces.includes(s)
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Diagnosis */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {t('dental.shortcuts.quickRegistration.diagnosis')}
            </label>
            <textarea
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              placeholder={t('dental.shortcuts.quickRegistration.diagnosis')}
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {t('dental.shortcuts.quickRegistration.price')}
            </label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              min="0"
              step="0.01"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </div>

          {/* Performer (read-only) */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {t('dental.shortcuts.quickRegistration.performer')}
            </label>
            <input
              type="text"
              readOnly
              value={user?.email ?? '—'}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
            />
          </div>

          {/* Visit number */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
              {t('dental.shortcuts.quickRegistration.visitNumber')}
            </label>
            <input
              type="number"
              value={visitNumber}
              min={1}
              onChange={(e) => setVisitNumber(Number(e.target.value))}
              className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </div>

          {/* Keep-open (batch mode) */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={keepOpen}
              onClick={() => setKeepOpen((v) => !v)}
              className={`relative h-5 w-9 rounded-full transition-colors ${keepOpen ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${keepOpen ? 'translate-x-4' : 'translate-x-0.5'}`}
              />
            </button>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t('dental.shortcuts.quickRegistration.keepOpen')}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            {t('dental.shortcuts.quickRegistration.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || teeth.length === 0}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('dental.shortcuts.quickRegistration.save')}
          </button>
        </div>
      </div>
    </>
  );
}
